import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  useStoreLocationsLazyQuery,
  StoreLocationsQueryVariables,
  useStoreLocationsQuery,
  StoreLocationsQuery,
} from '@ocx/graphql';
import { logInDev } from '@ocx/utils';
import { Coordinates, IStore, PriceType } from '@ocx-app/modules/stores/types';
import { getLocationNodes, mapLocationsData } from '@ocx-app/modules/stores/mapLocationsData';
import { messages } from '@ocx-app/modules/stores/messages';
import { formatPrice } from '@ocx-app/lib/formatters/formatPrice';
import { useConfiguration } from '@ocx/data-configuration';
import { checkLastItemsSame, checkMapPositionDiff, getSearchRadiusMeters } from '@ocx-app/modules/stores/utils';
import { usePrevCalculation } from '@ocx-app/hooks/usePrevCalculation';

import { STORES_CHUNK_SIZE } from '../store-locator.constants';

const buildQueryVariables = (params: {
  lat: number;
  lng: number;
  searchRadius: number;
  cursor?: string | null;
}): Partial<StoreLocationsQueryVariables> => ({
  first: STORES_CHUNK_SIZE,
  after: params.cursor || null,
  filter: {
    geoLocation: {
      epicenter: { latitude: params.lat, longitude: params.lng },
      radiusInMeters: params.searchRadius,
    },
  },
});

export interface IUseStoreLocatorController {
  handleZoom(): void;
  loading: boolean;
  stores: IStore[];
  isLoadMoreAvailable: boolean;
  handleLoadMore(): void;
}

type UseStoreLocatorControllerProps = {
  // eslint-disable-next-line no-undef
  map: google.maps.Map | null;
  targetLocation: Coordinates;
  currentLocation: Coordinates;
  navigateToPosition(coordinates: Coordinates): void;
  specificStoreUUID?: string;
  setSelectedStore: (store: IStore) => void;
};

export const useStoreLocatorController = ({
  map,
  targetLocation,
  currentLocation,
  navigateToPosition,
  specificStoreUUID,
  setSelectedStore,
}: UseStoreLocatorControllerProps): IUseStoreLocatorController => {
  const intl = useIntl();
  const fuelGradesConfig = useConfiguration('localRetailerConfig.fuelGrades');
  const fuelPricesMode = useConfiguration('storeLocator.fuelPricesMode');

  const epicenter = usePrevCalculation<Coordinates>((prev) => {
    const isDiff = checkMapPositionDiff(targetLocation, prev, getSearchRadiusMeters(map));
    return isDiff ? targetLocation : prev;
  }, targetLocation);

  const [loadStores, { loading, data: storesData }] = useStoreLocationsLazyQuery();

  const priceTypeTitles = useMemo(
    () => ({
      [PriceType.CASH]: intl.formatMessage(messages.priceTypeCash),
      [PriceType.CREDIT]: intl.formatMessage(messages.priceTypeCredit),
    }),
    [intl],
  );

  const onSpecificStoreCompleted = useCallback(
    (data: StoreLocationsQuery) => {
      if (!data.stores?.edges) {
        return;
      }
      const nodes = getLocationNodes(data);
      const specificStores = mapLocationsData(
        nodes,
        targetLocation,
        currentLocation,
        formatPrice,
        fuelGradesConfig,
        fuelPricesMode,
        priceTypeTitles,
      );

      if (!specificStores.length) {
        return;
      }

      setSelectedStore(specificStores[0]);
    },
    [currentLocation, fuelGradesConfig, fuelPricesMode, priceTypeTitles, setSelectedStore, targetLocation],
  );
  const { loading: specificStoreLoading } = useStoreLocationsQuery({
    skip: !specificStoreUUID,
    variables: {
      filter: {
        uuidEquals: specificStoreUUID,
      },
    },
    onCompleted: onSpecificStoreCompleted,
  });

  const stores = usePrevCalculation<IStore[]>((prev) => {
    if (loading || specificStoreLoading) {
      return prev;
    }
    const nodes = getLocationNodes(storesData);
    if (storesData?.stores?.pageInfo.hasPreviousPage) {
      const newData = mapLocationsData(
        nodes,
        targetLocation,
        currentLocation,
        formatPrice,
        fuelGradesConfig,
        fuelPricesMode,
        priceTypeTitles,
      );
      if (checkLastItemsSame(prev, newData)) {
        return prev;
      }
      return [...prev, ...newData];
    }
    return mapLocationsData(
      nodes,
      targetLocation,
      currentLocation,
      formatPrice,
      fuelGradesConfig,
      fuelPricesMode,
      priceTypeTitles,
    );
  }, []);

  useEffect(() => {
    loadStores({
      variables: buildQueryVariables({
        searchRadius: getSearchRadiusMeters(map),
        cursor: null,
        lat: epicenter.lat,
        lng: epicenter.lng,
      }),
    }).catch(logInDev);
  }, [epicenter.lat, epicenter.lng, loadStores, map]);

  const handleLoadMore = useMemo(() => {
    if (storesData?.stores?.pageInfo.hasNextPage !== true) {
      return () => undefined;
    }

    const { endCursor } = storesData.stores.pageInfo;
    return () =>
      loadStores({
        variables: buildQueryVariables({
          searchRadius: getSearchRadiusMeters(map),
          cursor: endCursor,
          lat: epicenter.lat,
          lng: epicenter.lng,
        }),
      }).catch(logInDev);
  }, [epicenter.lat, epicenter.lng, loadStores, map, storesData?.stores?.pageInfo]);

  const handleZoom = useCallback(() => {
    if (!map) {
      return;
    }
    const center = map.getCenter();
    if (center) {
      navigateToPosition({ lat: center.lat(), lng: center.lng() });
    }
  }, [map, navigateToPosition]);

  return {
    handleZoom,
    stores,
    loading: loading || specificStoreLoading,
    handleLoadMore,
    isLoadMoreAvailable: storesData?.stores?.pageInfo.hasNextPage || false,
  };
};
